import { useCallback, useEffect, useRef } from 'react';

/**
 * Poll `refresh` every `interval` ms while the page is visible.
 *
 * - Pauses while the tab is hidden or offline; fetches immediately when the
 *   tab becomes visible again, on window focus, and on the "online" event.
 * - Never piles up requests: ticks while a request is in flight are coalesced
 *   into one immediate follow-up refresh after it settles.
 * - `refresh({ background })` is expected to keep the UI quiet for background
 *   polls (no loading skeletons, no flicker) and to resolve even on failure.
 *
 * `refreshKey` re-runs the setup effect (e.g. when filters change) and does an
 * immediate non-background fetch, mirroring a mount.
 *
 * Returns `refreshNow(background)` for caller-triggered refreshes.
 */
export function useAutoRefresh(
  refresh,
  { interval = 5000, refreshKey, enabled = true } = {},
) {
  const refreshRef = useRef(refresh);
  const inFlightRef = useRef(false);
  const pendingImmediateRef = useRef(false);

  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  const refreshNow = useCallback(async (background = true) => {
    if (typeof document === 'undefined') return;
    let asBackground = background;
    for (;;) {
      if (document.visibilityState === 'hidden' || !navigator.onLine) {
        pendingImmediateRef.current = true;
        return;
      }
      if (inFlightRef.current) {
        pendingImmediateRef.current = true;
        return;
      }
      pendingImmediateRef.current = false;
      inFlightRef.current = true;
      try {
        await refreshRef.current({ background: asBackground });
      } finally {
        inFlightRef.current = false;
      }
      if (
        !pendingImmediateRef.current
        || document.visibilityState === 'hidden'
        || !navigator.onLine
      ) {
        return;
      }
      // A refresh was requested while we were in flight; run one follow-up.
      asBackground = true;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    const handleVisible = () => {
      if (document.visibilityState === 'visible') void refreshNow(true);
    };
    const handleOnline = () => void refreshNow(true);

    const timer = window.setInterval(() => {
      if (
        document.visibilityState !== 'hidden'
        && navigator.onLine
        && !inFlightRef.current
      ) {
        void refreshNow(true);
      }
    }, interval);

    document.addEventListener('visibilitychange', handleVisible);
    window.addEventListener('focus', handleVisible);
    window.addEventListener('online', handleOnline);
    void refreshNow(false);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisible);
      window.removeEventListener('focus', handleVisible);
      window.removeEventListener('online', handleOnline);
    };
  }, [enabled, interval, refreshKey, refreshNow]);

  return refreshNow;
}
